/**
 * Gallery <-> master-event resolution.
 *
 * Indexed gallery entries have arrived over the years from three different
 * paths (hand-edited Strapi JSON, the old scraper, the Lambda indexer), so the
 * master event can be encoded in any of these ways:
 *
 *   1. a "[Master Event/Year]" suffix baked into the Title
 *   2. a numeric `master_event` id on the entry
 *   3. a `master_event` name string on the entry
 *   4. nothing at all, with only the Title hinting at the event
 *
 * The old logic only handled (1), matched the name case-sensitively, and
 * overwrote a perfectly good id with `undefined` whenever the suffix did not
 * match exactly. This resolver tries every encoding, never downgrades a
 * resolved id to nothing, and falls back to matching the title itself.
 */

export const OTHER_MASTER_EVENT_ID = "__other";
export const OTHER_MASTER_EVENT_NAME = "Other";

const norm = (s) =>
  String(s == null ? "" : s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

// Known shorthands and legacy names, matched against the normalised suffix or
// title. Order matters: the first match wins.
const ALIASES = [
  [/beyonddimension|gyproc|^bd$/, "Beyond Dimensions with Gyproc"],
  [/abid/, "ABID IDEA Awards"],
  [/saintgobain|sgscholar|architecturestudentcontest|^sg$|^asc/, "Saint Gobain Scholarships"],
  [/archumen/, "Archumen"],
  [/transparence/, "Transparence"],
  [/igbc/, "IGBC"],
  [/ayda/, "AYDA"],
  [/bendingmoment/, "Bending Moment"],
  [/designaddvance/, "Designaddvance"],
  [/urbaninnovation/, "Urban Innovation Challenge"],
  [/nasabridge|^bridge/, "Bridge"],
];

/** Strip the "[Event/Year]" suffix, tolerating stray or doubled brackets. */
function parseSuffix(rawTitle) {
  const title = String(rawTitle || "").trim();
  const match = title.match(/\[+([^[\]]*)\]+/);
  if (!match) return { title, suffixName: "", suffixYear: null };

  const [suffixName, suffixYear] = match[1].split("/");
  return {
    title: title.replace(match[0], "").trim(),
    suffixName: (suffixName || "").trim(),
    suffixYear: suffixYear ? parseInt(suffixYear, 10) : null,
  };
}

function buildIndex(masterEvents) {
  const byId = {};
  const byName = {};
  (masterEvents || []).forEach((me) => {
    byId[me.id] = me;
    byName[norm(me.name)] = me.id;
  });
  // Longest names first so "Beyond Dimensions with Gyproc" wins over "Bridge".
  const byLength = (masterEvents || [])
    .slice()
    .sort((a, b) => String(b.name).length - String(a.name).length);
  return { byId, byName, byLength };
}

function fromAlias(text, byName) {
  const n = norm(text);
  if (!n) return null;
  for (const [pattern, name] of ALIASES) {
    if (pattern.test(n)) {
      const id = byName[norm(name)];
      if (id !== undefined) return id;
    }
  }
  return null;
}

/** Any master-event name contained in the text, longest name first. */
function fromSubstring(text, byLength) {
  const n = norm(text);
  if (!n) return null;
  for (const me of byLength) {
    const mn = norm(me.name);
    if (mn && n.includes(mn)) return me.id;
  }
  return null;
}

function guessYear(title, suffixYear) {
  if (suffixYear) return suffixYear;
  const t = String(title || "");

  const full = t.match(/\b(19|20)\d{2}\b/);
  if (full) return parseInt(full[0], 10);

  // Academic-year forms: "24-25", "2024-25", "1415", "2K15".
  const k = t.match(/\b2[kK](\d{2})\b/);
  if (k) return 2000 + parseInt(k[1], 10);

  const span = t.match(/\b(\d{2})\s*[-–/]?\s*(\d{2})\b/);
  if (span) {
    const a = parseInt(span[1], 10);
    const b = parseInt(span[2], 10);
    if (b === a + 1) return 2000 + b;
  }
  return null;
}

/**
 * Resolve one indexed gallery entry.
 * Returns { Title, master_event, year } with master_event either a real
 * master-event id or OTHER_MASTER_EVENT_ID.
 */
export function resolveGalleryMeta(gallery, masterEvents) {
  const { byId, byName, byLength } = buildIndex(masterEvents);
  const { title, suffixName, suffixYear } = parseSuffix(gallery && gallery.Title);
  const stored = gallery ? gallery.master_event : null;

  let id = null;

  if (suffixName) {
    id = byName[norm(suffixName)];
    if (id === undefined) id = fromAlias(suffixName, byName);
  }

  if (id === null || id === undefined) {
    if (typeof stored === "number" && byId[stored]) id = stored;
    else if (typeof stored === "string" && stored.trim()) {
      id = byName[norm(stored)];
      if (id === undefined) id = fromAlias(stored, byName);
    }
  }

  if (id === null || id === undefined) id = fromAlias(title, byName);
  if (id === null || id === undefined) id = fromSubstring(title, byLength);

  return {
    Title: title,
    master_event: id === null || id === undefined ? OTHER_MASTER_EVENT_ID : id,
    year: guessYear(title, suffixYear),
  };
}
